import React from 'react';
import { withRouter } from 'react-router';
import { Route, NavLink, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import camelcase from 'lodash/camelCase';
import get from 'lodash/get';
import { defineMessages, injectIntl } from 'react-intl';

import Tab from '../../components/Tab';
import './styles.css';

import withHostContext, { trackView, trackViewGa } from '../../context/host/consumer';
import TransferMenu from '../TransferMenu';
import OverviewMenu from '../OverviewMenu';
import StudiesMenu from '../StudiesMenu';
import StudentLifeMenu from '../StudentLifeMenu';
import CostsMenu from '../CostsMenu';
import ProfilesMenu from '../ProfilesMenu';
import AdmissionsMenu from '../AdmissionsMenu';

import { validRoutes } from '../../constants';
import { scrollToHash, getBaseUrl } from '../../utils/url';
import { getAdmissionContact } from '../../utils/admission';

const messages = defineMessages({
  overview: {
    id: 'hubs.navtabs.overview',
    defaultMessage: 'Overview',
  },
  studies: {
    id: 'hubs.navtabs.studies',
    defaultMessage: 'Studies',
  },
  studentLife: {
    id: 'hubs.navtabs.student-life',
    defaultMessage: 'Student Life',
  },
  admissions: {
    id: 'hubs.navtabs.admissions',
    defaultMessage: 'Admissions',
  },
  costs: {
    id: 'hubs.navtabs.costs',
    defaultMessage: 'Costs',
  },
  profiles: {
    id: 'hubs.navtabs.profiles',
    defaultMessage: 'Profiles',
  },
  spotlights: {
    id: 'hubs.navtabs.spotlights',
    defaultMessage: 'Spotlights',
  },
  transfer: {
    id: 'hubs.navtabs.transfer',
    defaultMessage: 'Transfer',
  },
});

class MenuNavBar extends React.Component {
  static propTypes = {
    collegeData: PropTypes.shape({
      ipedscode: PropTypes.string,
      logo: PropTypes.string,
      profile: PropTypes.object,
      webTour: PropTypes.object,
      webLink: PropTypes.object,
      fullProfile: PropTypes.object,
    }),
    isEnhanced: PropTypes.bool,
    userData: PropTypes.object,
    testScores: PropTypes.arrayOf(PropTypes.object),
    collegeContact: PropTypes.array,
    applicationStatistics: PropTypes.object,
    sendEmail: PropTypes.func,
    tuitionStatistics: PropTypes.object,
    highSchool: PropTypes.object,
    enableSticky: PropTypes.bool,
    isEditMode: PropTypes.bool,
    deliveryType: PropTypes.string,
    isApplyingTo: PropTypes.bool,
    intl: PropTypes.object,
    session: PropTypes.object,
    apiUrl: PropTypes.string,
    scid: PropTypes.number,
    clickTrackingEvents: PropTypes.func,
    permissions: PropTypes.object,
    featureFlags: PropTypes.shape({
      releaseIntHemCommonAppDeadlines: PropTypes.bool,
    }),
  };

  premium = false;
  lastTab = '';

  constructor(props) {
    super(props);
    this.state = {
      activeIndex: this.getActiveIndex(window.location.pathname),
      initialLocation: { ...window.location },
    };
    const { profile, webLink, webTour } = this.props.collegeData;
    /* istanbul ignore next */
    this.premium =
      get(profile, 'custom_profiles', []).length > 0 ||
      get(webLink, 'premium_links', []).length > 0 ||
      get(webTour, 'campus_media.assets', []).length > 0;
    this.lastTab = '';
  }

  degreesOfferedFilter = 'all';

  componentDidMount() {
    this.validateRoute();
  }

  componentDidUpdate(prevProps) {
    /* istanbul ignore else */
    if (this.props.location !== prevProps.location) {
      this.validateRoute();
    }
  }

  validateRoute = () => {
    const { location, history } = this.props;

    if (!validRoutes.find((route) => location.pathname.includes(route))) {
      history.push(`${this.getInitialPathName()}Overview`);
    }

    scrollToHash(this.props.location);
  };

  getDegreesOfferedFilter = () => {
    return this.degreesOfferedFilter;
  };

  /* istanbul ignore next */
  setDegreesOfferedFilter = (filter) => {
    this.degreesOfferedFilter = filter;
  };

  getInitialPathName = () => {
    const {
      initialLocation: { pathname },
    } = this.state;

    return getBaseUrl(pathname);
  };

  getActiveIndex = (pathname) => {
    const tabIndex = this.getPanes(pathname).findIndex((pane) => {
      // return this.getPanes(pathname).findIndex(pane => {
      return !!matchPath(window.location.pathname, {
        path: pane.menuItem.to,
        exact: true,
      });
    });
    const tab = this.getPanes(window.location.pathname, tabIndex)[tabIndex];
    if (tab) {
      const location = tab.menuItem['data-clicktrack'];
      if (location !== this.lastTab) {
        this.lastTab = location;
        trackView(this.props.hostContext, { location: camelcase(location) });
        trackViewGa(this.props.hostContext, { location, premium: this.premium });
      }
    }
    return tabIndex;
  };

  getMenuItem = ({
    tabName,
    baseUrl,
    message,
    selected,
    clicktrackMessage = message,
  }) => {
    return {
      as: NavLink,
      content: this.props.intl.formatMessage(message),
      to: `${baseUrl}${tabName.replace(' ', '_')}`,
      exact: true,
      key: tabName.toLowerCase().replace(' ', ''),
      role: 'tab',
      'aria-label': tabName,
      'aria-selected': selected,
      'data-clicktrack': this.props.intl.formatMessage(clicktrackMessage),
    };
  };

  getPanes = (pathname, activeIndex = -1) => {
    const {
      collegeData,
      isEnhanced,
      userData,
      testScores,
      collegeContact,
      sendEmail,
      tuitionStatistics,
      highschoolCollegeInfo,
      highSchool,
      applicationStatistics,
      apiUrl,
      session,
      isEditMode,
      deliveryType,
      isApplyingTo,
      fetchCollegeEvents,
      scid,
      visits,
      clickTrackingEvents,
      fetchCounselorComments,
      permissions,
      featureFlags,
      hostContext,
    } = this.props;
    const collegeName = get(collegeData, 'fullProfile.overview.schoolName.value', '');
    const collegeState = get(
      collegeData,
      'fullProfile.overview.admissionsOffice.state.value',
      '-',
    );
    const scholarships = get(collegeData, 'fullProfile.scholarships');
    const baseUrl = pathname ? pathname : this.getInitialPathName();
    const overlaps = get(highschoolCollegeInfo, 'overlaps', {});
    const undergraduateEnrollment = get(
      collegeData,
      'fullProfile.overview.undergraduateEnrollment',
      {},
    );
    const profiles = get(collegeData, 'profile.custom_profiles', []);
    const studentProfile = profiles.find(
      (profile) => profile && profile.type === 'student',
    );
    const isCamino = get(hostContext, 'isCamino', false);
    const transferAdmissions = get(collegeData, 'fullProfile.transferAdmissions');
    const isCommonAppDeadlinesFlagOn =
      featureFlags?.releaseIntHemCommonAppDeadlines === true;
    const paneConfigs = {
      admissions: {
        menuItem: {
          tabName: 'Admissions',
          baseUrl,
          message: messages.admissions,
        },
        render: () => (
          <Route path={`${baseUrl}Admissions`}>
            <AdmissionsMenu
              admissionsData={get(collegeData, 'fullProfile.admissions')}
              admissionsContact={getAdmissionContact(collegeData)}
              highSchool={highSchool}
              applicationStatistics={applicationStatistics}
              deliveryType={deliveryType}
              isApplyingTo={isApplyingTo}
              websiteUrl={get(collegeData, 'fullProfile.overview.website.value', '')}
              collegeName={collegeName}
              userData={userData}
              isCommonAppDeadlinesFlagOn={isCommonAppDeadlinesFlagOn}
              featureFlags={featureFlags}
            />
          </Route>
        ),
      },
      costs: {
        menuItem: {
          tabName: 'Costs',
          baseUrl,
          message: messages.costs,
        },
        render: () => (
          <Route path={`${baseUrl}Costs`}>
            <CostsMenu
              applicationStatistics={applicationStatistics}
              highSchool={highSchool}
              session={session}
              scid={scid}
              costs={get(collegeData, 'fullProfile.costs')}
              tuitionStatistics={tuitionStatistics}
              transferAdmissions={transferAdmissions}
              collegeState={collegeState}
              scholarships={scholarships}
              userData={userData}
              testScores={testScores}
              featureFlags={featureFlags}
              permissions={permissions}
            />
          </Route>
        ),
      },
      overview: {
        menuItem: {
          tabName: 'Overview',
          baseUrl,
          message: messages.overview,
        },
        render: () => (
          <Route path={`${baseUrl}Overview'`}>
            <OverviewMenu
              collegeData={collegeData}
              isEnhanced={isEnhanced}
              userData={userData}
              testScores={testScores}
              collegeContact={collegeContact}
              overlaps={overlaps}
              sendEmail={sendEmail}
              apiUrl={apiUrl}
              session={session}
              scid={scid}
              isEditMode={isEditMode}
              fetchCollegeEvents={fetchCollegeEvents}
              applicationStatistics={applicationStatistics}
              visits={visits}
              highSchool={highSchool}
              clickTrackingEvents={clickTrackingEvents}
              fetchCounselorComments={fetchCounselorComments}
              permissions={permissions}
              featureFlags={featureFlags}
            />
          </Route>
        ),
      },
      spotlights: {
        menuItem: {
          tabName: 'Spotlights',
          baseUrl,
          message: messages.spotlights,
          clicktrackMessage: messages.profiles,
        },
        render: () => (
          <Route path={`${baseUrl}Spotlights`}>
            <ProfilesMenu profile={collegeData.profile} collegeName={collegeName} />
          </Route>
        ),
      },
      studies: {
        menuItem: {
          tabName: 'Studies',
          baseUrl,
          message: messages.studies,
        },
        render: () => (
          <Route path={`${baseUrl}Studies`}>
            <StudiesMenu
              profile={collegeData.profile}
              collegeName={collegeName}
              studiesData={collegeData.fullProfile.studies}
              lastUpdated={get(
                collegeData,
                'fullProfile.admissions.lastUpdated.value',
                '',
              )}
              getDegreesOfferedFilter={this.getDegreesOfferedFilter}
              setDegreesOfferedFilter={this.setDegreesOfferedFilter}
            />
          </Route>
        ),
      },
      studentLife: {
        menuItem: {
          tabName: 'Student Life',
          baseUrl,
          message: messages.studentLife,
        },
        render: () => (
          <Route path={`${baseUrl}Student_Life`}>
            <StudentLifeMenu
              profile={collegeData.profile}
              collegeName={collegeName}
              undergraduateEnrollment={undergraduateEnrollment}
              studentLifeData={collegeData.fullProfile.studentLife}
              highSchool={highSchool}
            />
          </Route>
        ),
      },
      transfer: {
        menuItem: {
          tabName: 'Transfer',
          baseUrl,
          message: messages.transfer,
        },
        render: () => (
          <Route path={`${baseUrl}Transfer`}>
            <TransferMenu collegeData={collegeData} />
          </Route>
        ),
      },
    };

    let paneKeys = ['overview', 'studies', 'studentLife', 'costs'];

    if (studentProfile) {
      paneKeys.push('spotlights');
    }

    if (isCamino) {
      paneKeys.splice(1, 0, 'transfer');
    } else {
      paneKeys.splice(3, 0, 'admissions');
    }

    return paneKeys.map((key, index) => {
      const config = paneConfigs[key];
      const menuItem = this.getMenuItem({
        ...config.menuItem,
        selected: index === activeIndex,
      });

      return {
        menuItem,
        render: config.render,
      };
    });
  };

  handleTabChange = (e, { activeIndex }) => {
    const panes = this.getPanes(null, activeIndex);
    const location =
      activeIndex !== -1
        ? panes[activeIndex].menuItem['data-clicktrack'] /* istanbul ignore next */
        : panes[0].menuItem['data-clicktrack'];
    /* istanbul ignore else */
    if (location !== this.lastTab) {
      this.lastTab = location;
      trackView(this.props.hostContext, { location: camelcase(location) });
      trackViewGa(this.props.hostContext, { location });
    }
    this.setState({
      activeIndex,
    });
  };

  render() {
    const { enableSticky } = this.props;
    /**
     * Required to get a correct tab opened by default
     * and as a check against browser back & forward buttons use.
     */
    const activeIndex = this.getActiveIndex();
    const currentActiveIndex =
      activeIndex === this.state.activeIndex ? this.state.activeIndex : activeIndex;
    const panes = this.getPanes(null, currentActiveIndex);

    return (
      <div className="navbar-container">
        <Tab
          className="navbar-tabs"
          data-cy="navbar-tabs"
          menu={{
            secondary: true,
            pointing: true,
            role: 'tablist',
            className: enableSticky ? 'sticky' : '',
          }}
          panes={panes}
          onTabChange={this.handleTabChange}
          activeIndex={currentActiveIndex}
        />
      </div>
    );
  }
}

export default withHostContext(withRouter(injectIntl(MenuNavBar)));
